/**
 * The one place the Channels screen talks to sam-dbam.
 *
 * Every request goes straight from the browser to sam-dbam and carries
 * `credentials: 'include'`, so the Hanko session cookie rides with it. There is
 * no proxy in between, and that is deliberate. The channel endpoints refuse the
 * header identity chain the rest of the service accepts, because a header is
 * whatever the sender typed. A proxy that forgot to forward the cookie would
 * quietly fall back to that chain, and the permission rule would stop meaning
 * anything.
 *
 * Every list function returns an array, always. A caller drawing a table should
 * not have to guard against a shape the service is not supposed to send.
 */

import { getDbamBase } from '../contexts/AuthContext';
import { SIGNED_OUT_PREVIEW, WORKING_ORG_ID } from '../config';
import { getSessionToken } from './session';

let unauthorizedHandler = null;
let healthRefreshInFlight = null;

/**
 * Thrown when the screen is running without sign-in.
 *
 * It is a refusal to ask, not a failure to answer. Every endpoint here needs a
 * verified session, and a page with no session has nothing to send. Making the
 * request anyway would fill the console with errors that look like faults in
 * this screen, when the truth is that nobody is signed in.
 */
export const NOT_SIGNED_IN = 'NOT_SIGNED_IN';

/**
 * Register what to do when the session turns out to have ended.
 *
 * A 401 from any call is the earliest reliable sign, and earlier than the
 * sign-in provider's own event. Passing null clears the handler.
 */
export function setUnauthorizedHandler(fn) {
    unauthorizedHandler = typeof fn === 'function' ? fn : null;
}

/** Anything that is not already a list becomes an empty one. */
function asArray(value) {
    return Array.isArray(value) ? value : [];
}

/**
 * One request, one set of rules for what a failure means.
 *
 * The service answers a refusal with a sentence written for a person, plus a
 * short code. Both are carried onto the thrown error, so a panel can show the
 * sentence and a caller can still branch on the code without reading English.
 */
async function request(path, options = {}) {
    // A token pasted into this tab is a real session. It is verified against the
    // same key set and answers to the same permission rules as the cookie, so
    // carrying it is not a shortcut around anything.
    const bearer = getSessionToken();

    if (SIGNED_OUT_PREVIEW && !bearer) {
        const err = new Error('This needs a signed-in session, and there is not one.');
        err.code = NOT_SIGNED_IN;
        throw err;
    }

    const headers = { ...options.headers };
    if (bearer) headers.Authorization = `Bearer ${bearer}`;

    // Naming the account is a choice between accounts the signed-in person
    // already belongs to. The service checks that membership before it accepts
    // the name, so this cannot reach an account they are not in.
    if (WORKING_ORG_ID) headers['X-Org-ID'] = WORKING_ORG_ID;
    let body = options.body;

    if (body !== undefined && body !== null && typeof body === 'object' && !(body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(body);
    }

    const response = await fetch(`${getDbamBase()}${path}`, {
        ...options,
        headers,
        body,
        credentials: 'include',
    });

    if (response.status === 401) {
        if (unauthorizedHandler) unauthorizedHandler();
        const err = new Error('Your session has ended. Sign in again.');
        err.status = 401;
        throw err;
    }

    if (response.status === 204) return null;

    let data = null;
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        try {
            data = await response.json();
        } catch (parseError) {
            data = null;
        }
    }

    if (!response.ok) {
        const nestedError = data?.error && typeof data.error === 'object' ? data.error : null;
        const legacyError = typeof data?.error === 'string' ? data.error : '';
        const err = new Error(nestedError?.message || data?.message || legacyError || `Request failed (${response.status})`);
        err.status = response.status;
        if (nestedError?.code || data?.code) err.code = nestedError?.code || data.code;
        throw err;
    }

    // sam-dbam's shared response contract is {data,meta}. A few legacy routes
    // still answer with a bare payload, so unwrap only when the envelope is
    // actually present. Keeping this at the transport boundary prevents every
    // panel from accidentally treating metadata as account data.
    if (data && Object.prototype.hasOwnProperty.call(data, 'data') && Object.prototype.hasOwnProperty.call(data, 'meta')) {
        return data.data;
    }
    return data;
}

/** Builds `?limit=&offset=` and leaves out anything nobody asked for. */
function pageQuery({ limit, offset, search } = {}) {
    const params = new URLSearchParams();
    if (limit !== undefined && limit !== null) params.append('limit', String(limit));
    if (offset !== undefined && offset !== null) params.append('offset', String(offset));
    if (search) params.append('search', search);
    const query = params.toString();
    return query ? `?${query}` : '';
}

// ── Bindings ────────────────────────────────────────────────────────────────
// The binding list on the Channels view, and the provisioning overlay.

export async function listBindings() {
    const body = await request('/dbam/v1/channels/bindings');
    return asArray(body?.bindings);
}

const HEALTH_VALUES = new Set(['healthy', 'degraded', 'unavailable', 'unknown']);
const REQUEST_VALUES = new Set(['none', 'pending', 'acknowledged', 'approved', 'declined', 'cancelled']);
const REQUEST_KEY_PREFIX = 'channels.enablement-request.v1';

function requestStorageKey(channel) {
    return `${REQUEST_KEY_PREFIX}.${WORKING_ORG_ID || 'current'}.${channel}`;
}

function requestKey(channel) {
    const storageKey = requestStorageKey(channel);
    try {
        const existing = window.localStorage.getItem(storageKey);
        if (existing) return existing;
        const created = window.crypto?.randomUUID?.()
            || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        window.localStorage.setItem(storageKey, created);
        return created;
    } catch (_) {
        return window.crypto?.randomUUID?.()
            || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
}

function clearResolvedRequestKey(channel, state, enabled) {
    // The catalogue currently returns only an unresolved request. Therefore an
    // enabled channel with no request is also the observable approved outcome.
    if (enabled !== true && !['approved', 'declined', 'cancelled'].includes(state)) return;
    try {
        window.localStorage.removeItem(requestStorageKey(channel));
    } catch (_) {
        // Storage is an optimization for browser retries, not an availability dependency.
    }
}

/**
 * Keep the organization control contract at the transport boundary. Missing or
 * malformed enablement never becomes an inferred yes. The server's last
 * completed diagnostic is preserved even when stale; the background sweep is
 * responsible for replacing it, and the display projects absence to Failed.
 */
export function normalizeChannelControl(raw = {}) {
    const channel = raw.channel || raw.channel_id || raw.channel_type || '';
    const enablementState = String(raw.enablement_state || '').toLowerCase();
    const enabled = typeof raw.enabled === 'boolean'
        ? raw.enabled
        : enablementState === 'enabled'
            ? true
            : enablementState === 'disabled'
                ? false
                : null;
    const health = String(raw.runtime_health || raw.health || 'unknown').toLowerCase();
    const request = raw.enablement_request || raw.request || {};
    const requestState = String(request.status || request.state || raw.request_state || 'none').toLowerCase();
    return {
        channel,
        enabled,
        runtime_health: HEALTH_VALUES.has(health) ? health : 'unknown',
        runtime_health_stale: Boolean(raw.runtime_health_stale),
        runtime_health_reason: raw.runtime_health_reason || raw.health_reason || '',
        runtime_health_checked_at: raw.runtime_health_checked_at || raw.health_checked_at || null,
        runtime_health_expires_at: raw.runtime_health_expires_at || null,
        runtime_health_source: raw.runtime_health_source || '',
        // The reading's own detail, as the service recorded it. sam-dbam omits
        // it when the reading is absent or stale, for the same reason the health
        // then reads "unknown": a detail table from an expired reading would be
        // shown as the channel's current state.
        runtime_health_evidence: raw.runtime_health_evidence || null,
        enablement_request: {
            state: REQUEST_VALUES.has(requestState) ? requestState : 'none',
            requested_at: request.requested_at || raw.requested_at || null,
            acknowledged_at: request.acknowledged_at || raw.acknowledged_at || null,
            resolved_at: request.resolved_at || raw.resolved_at || null,
        },
    };
}

export async function getChannelControlStatus() {
    const body = await request('/dbam/v1/channels/control-status');
    const rows = asArray(body?.channels || body);
    return rows.map(normalizeChannelControl).filter((row) => {
        if (!row.channel) return false;
        clearResolvedRequestKey(row.channel, row.enablement_request.state, row.enabled);
        return true;
    });
}

// One sweep per tab at a time. The endpoint holds the request until its worker
// finishes so a Cloud Run instance cannot be suspended mid-check.
export function refreshChannelHealth() {
    if (!healthRefreshInFlight) {
        healthRefreshInFlight = request('/dbam/v1/channels/runtime-health/refresh', { method: 'POST' })
            .finally(() => { healthRefreshInFlight = null; });
    }
    return healthRefreshInFlight;
}

export async function requestChannelEnablement(channel) {
    let body;
    try {
        body = await request(`/dbam/v1/channels/${encodeURIComponent(channel)}/enablement-requests`, {
            method: 'POST',
            body: {
                idempotency_key: requestKey(channel),
                request_reason: 'Requested from the customer Channels screen.',
            },
        });
    } catch (err) {
        // Only the explicit already-enabled code is a settled outcome. Other
        // 409s (notably idempotency conflicts) remain actionable failures.
        if (err.status === 409 && err.code === 'CHANNEL_ALREADY_ENABLED') {
            clearResolvedRequestKey(channel, 'approved', true);
            return {
                channel,
                enabled: true,
                already_enabled: true,
                enablement_request: { state: 'none', requested_at: null, acknowledged_at: null, resolved_at: null },
            };
        }
        throw err;
    }
    const requestBody = body?.request || body;
    return {
        channel,
        enablement_request: normalizeChannelControl({ channel, enablement_request: requestBody }).enablement_request,
    };
}

export async function createBinding(body) {
    return request('/dbam/v1/channels/bindings', { method: 'POST', body });
}

export async function transitionBinding(id, state, reason) {
    return request(`/dbam/v1/channels/bindings/${encodeURIComponent(id)}/transition`, {
        method: 'POST',
        body: { state, reason },
    });
}

export async function stopBinding(id) {
    return request(`/dbam/v1/channels/bindings/${encodeURIComponent(id)}/stop`, {
        method: 'POST',
    });
}

export async function setCredentialRef(id, credentialRef, expiresAt) {
    return request(`/dbam/v1/channels/bindings/${encodeURIComponent(id)}/credential-ref`, {
        method: 'POST',
        body: { credential_ref: credentialRef, expires_at: expiresAt },
    });
}

/**
 * What the platform knows this identity by: a sending domain, an SMTP host, a
 * page id. The service refuses anything whose name looks like a secret, so a
 * password sent here comes back as a 400 rather than being quietly stored.
 */
export async function setBindingIdentifiers(id, identifiers) {
    return request(`/dbam/v1/channels/bindings/${encodeURIComponent(id)}/identifiers`, {
        method: 'POST',
        body: { platform_identifiers: identifiers },
    });
}

export async function setRouteKey(id, routeKey) {
    return request(`/dbam/v1/channels/bindings/${encodeURIComponent(id)}/route-key`, {
        method: 'POST',
        body: { webhook_route_key: routeKey },
    });
}

// ── Managed email channel ──────────────────────────────────────────────────
// Email is one organization facility, not an author-selected list of sender
// identities. These calls therefore use the explicit ec_binding_id state
// returned by the service rather than the generic binding editor above.

export async function getEmailChannel() {
    return request('/dbam/v1/channels/email');
}

export async function savePendingEmailConfiguration(body) {
    return request('/dbam/v1/channels/email/pending', { method: 'PUT', body });
}

export async function discardPendingEmailConfiguration() {
    return request('/dbam/v1/channels/email/pending', { method: 'DELETE' });
}

export async function activatePendingEmailConfiguration() {
    return request('/dbam/v1/channels/email/activate', { method: 'POST' });
}

export async function returnEmailToPlatform() {
    return request('/dbam/v1/channels/email/return-to-platform', { method: 'POST' });
}

export async function rollbackEmailConfiguration() {
    return request('/dbam/v1/channels/email/rollback', { method: 'POST' });
}

export async function startEmailProbe() {
    return request('/dbam/v1/channels/email/probes', { method: 'POST' });
}

export async function acquireEmailSendLease(body) {
    return request('/dbam/v1/channels/email/send-leases', { method: 'POST', body });
}

export async function releaseEmailSendLease(leaseId) {
    return request(`/dbam/v1/channels/email/send-leases/${encodeURIComponent(leaseId)}`, { method: 'DELETE' });
}

// ── Binding defaults ────────────────────────────────────────────────────────
// The defaults overlay, and the pre-filled values the send preview inherits.

export async function getBindingDefaults(id) {
    return request(`/dbam/v1/channels/bindings/${encodeURIComponent(id)}/defaults`);
}

export async function saveBindingDefaults(id, body) {
    return request(`/dbam/v1/channels/bindings/${encodeURIComponent(id)}/defaults`, {
        method: 'PUT',
        body,
    });
}

export async function listAllDefaults() {
    const body = await request('/dbam/v1/channels/defaults');
    return asArray(body?.defaults);
}

export async function setDefaultBinding(id) {
    return request(`/dbam/v1/channels/bindings/${encodeURIComponent(id)}/default`, { method: 'POST' });
}

export async function clearDefaultBinding(id) {
    return request(`/dbam/v1/channels/bindings/${encodeURIComponent(id)}/default`, { method: 'DELETE' });
}

// ── Test recipients ─────────────────────────────────────────────────────────
// The addresses the round-trip test sends to, listed in the defaults overlay.

export async function listTestRecipients(channel) {
    const query = channel ? `?channel=${encodeURIComponent(channel)}` : '';
    const body = await request(`/dbam/v1/channels/test-recipients${query}`);
    return asArray(body?.test_recipients);
}

export async function createTestRecipient(body) {
    return request('/dbam/v1/channels/test-recipients', { method: 'POST', body });
}

export async function deleteTestRecipient(id) {
    return request(`/dbam/v1/channels/test-recipients/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── History ─────────────────────────────────────────────────────────────────
// The Activity and Audit views. Both answer with their page and the total, so
// the paging bar can say "Page 2 of 5 · 187 total" without a second request.

export async function listChannelAudit(paging) {
    return request(`/dbam/v1/channels/audit${pageQuery(paging)}`);
}

export async function listChannelEvents(paging) {
    return request(`/dbam/v1/channels/events${pageQuery(paging)}`);
}

export async function getChannelOverview(period = 'month') {
    return request(`/dbam/v1/channels/overview?period=${encodeURIComponent(period)}`);
}

export async function getChannelAttention() {
    const body = await request('/dbam/v1/channels/attention');
    return asArray(body?.items);
}

export async function sendsByChannel(days) {
    const query = days !== undefined && days !== null ? `?days=${encodeURIComponent(days)}` : '';
    const body = await request(`/dbam/v1/channels/sends-by-channel${query}`);
    return asArray(body?.channels);
}

// ── Money ───────────────────────────────────────────────────────────────────
// Usage feeds the spend figures. The rate rows are what turn a message count
// into a price in the send preview.

export async function listUsage() {
    const body = await request('/dbam/v1/billing/usage');
    return asArray(body?.usage ?? body?.events);
}

export async function listRateSchedules(channel) {
    // The server prices per channel (schedules live in per-channel versions),
    // so the channel is required — without it the endpoint answers 400.
    const query = `?channel=${encodeURIComponent(channel)}`;
    const body = await request(`/dbam/v1/billing/rate-schedules${query}`);
    return asArray(body?.schedules ?? body?.rate_schedules);
}

export async function listRateRows(scheduleId) {
    const body = await request(`/dbam/v1/billing/rate-schedules/${encodeURIComponent(scheduleId)}/rows`);
    return asArray(body?.rows ?? body?.rate_rows);
}

// ── Email templates ─────────────────────────────────────────────────────────
// The invitation, reminder and completion emails. This is the store Console
// already edits. Surfacing the same one here is the point: two stores would
// mean two versions of one email and no way to tell which went out.

export async function listEmailTemplates(orgId) {
    const body = await request(`/dbam/v1/templates?org_id=${encodeURIComponent(orgId)}`);
    return asArray(body?.data ?? body);
}

export async function updateEmailTemplate(templateId, patch) {
    return request(`/dbam/v1/templates/${encodeURIComponent(templateId)}`, {
        method: 'PUT',
        body: patch,
    });
}

export async function createEmailTemplate(template) {
    return request('/dbam/v1/templates', { method: 'POST', body: template });
}

export async function deleteEmailTemplate(templateId) {
    return request(`/dbam/v1/templates/${encodeURIComponent(templateId)}`, {
        method: 'DELETE',
    });
}

// Organization branding used by the exact sent-mail preview. This is an
// existing sam-dbam organization API; the Templates panel never invents a
// logo or formal name when the session cannot read it.
export async function getOrganization(orgId) {
    return request(`/dbam/v1/organizations/${encodeURIComponent(orgId)}`);
}

// The short invitation/completion copy used by conversational channels. This
// is the same durable store formerly edited from Console Accounts > Templates.
export async function listChannelMessages(orgId, channel) {
    const params = new URLSearchParams({ org_id: orgId });
    if (channel) params.set('channel', channel);
    const body = await request(`/dbam/v1/channel-templates?${params.toString()}`);
    return asArray(body?.data ?? body);
}

export async function saveChannelMessage({ orgId, channel, type, bodyText, language = 'en' }) {
    return request('/dbam/v1/channel-templates', {
        method: 'PUT',
        body: {
            org_id: orgId,
            channel,
            type,
            language,
            body_text: bodyText || '',
        },
    });
}
